/**
 * 收货地址相关接口
 * 对应 Apipost: 移动用户端 > 用户 > 收货地址
 */

import { peiwantHttp } from "@/http/axios";

// ==================== 类型定义 ====================

/** 地址项 */
export interface AddressItem {
  id: number;
  real_name: string;   // 收货人姓名
  phone: string;       // 收货人电话
  province: string;    // 省
  city: string;        // 市
  district: string;    // 区/街道
  detail: string;      // 具体地址
  is_default: number;  // 是否默认：1默认 0非默认
  city_id: number;     // 城市ID
}

/** 地址列表响应 */
export interface AddressListResponse {
  status: number;
  msg: string;
  data: AddressItem[];
}

/** 添加/编辑地址请求参数 */
export interface AddressEditParams {
  /** 修改时传，新增不传 */
  id?: number;
  /** 收货人姓名 */
  real_name: string;
  /** 收货人电话 */
  phone: string;
  /** 具体地址 */
  detail: string;
  /** 是否阅读协议 */
  is_read_agreement?: number;
  /** 城市ID */
  city_id: number;
  /** 经度 */
  longitude?: string;
  /** 纬度 */
  latitude?: string;
  /** 地址信息(省市区) */
  address: {
    province: string;
    city: string;
    district: string;
    city_id: number;
  };
}

/** 通用成功响应 */
export interface SuccessResponse {
  status: number;
  msg: string;
}

/** 单个地址详情响应 */
export interface AddressDetailResponse {
  status: number;
  msg: string;
  data: AddressItem & {
    longitude?: string;
    latitude?: string;
  };
}

// ==================== 接口调用 ====================
// 注意：HTTP 204 空响应体已在 peiwantHttp 响应拦截器中统一转为 {status:204, msg:"success"}
// 所以以下接口可直接通过 res.data.status 判断成功/失败

/**
 * 接口1：获取地址列表
 * GET /api/address/list
 * Header: Authorization: {{user_token}}
 */
export async function fetchAddressList(): Promise<AddressItem[]> {
  console.log("[API] 获取地址列表:", "/api/address/list");

  const res = await peiwantHttp.get<AddressListResponse>("/api/address/list");
  console.log("[API] address/list 完整响应:", {
    httpStatus: res.status,
    headers: res.headers,
    data: res.data,
    hasData: !!(res.data as AddressListResponse)?.data,
    dataArray: (res.data as AddressListResponse)?.data,
    url: res.config?.url,
    baseURL: res.config?.baseURL,
  });

  const body = res.data as AddressListResponse;
  if (body?.status !== undefined && body.status !== 200 && body.status !== 204) {
    throw new Error(body?.msg || "获取地址列表失败");
  }

  return body?.data ?? [];
}

/**
 * 接口2：添加/编辑地址
 * POST /api/address/edit
 * Body参数(form-data):
 *   - id: 修改时传，新增不传
 *   - real_name: 收货人姓名
 *   - phone: 收货人电话
 *   - detail: 具体地址
 *   - address[province]: 省
 *   - address[city]: 市
 *   - address[district]: 区
 *   - address[city_id]: 城市ID
 *   - is_read_agreement: 是否阅读协议(可选)
 *   - longitude: 经度(可选)
 *   - latitude: 纬度(可选)
 *
 * 注意：该接口使用 form-data 格式提交
 */
export async function saveAddress(params: AddressEditParams): Promise<SuccessResponse["msg"]> {
  console.log("[API] 添加/编辑地址:", "/api/address/edit", params.id ? "编辑" : "新增");

  // 使用 FormData 提交
  const formData = new FormData();

  // 编辑时传id
  if (params.id) {
    formData.append("id", String(params.id));
  }

  formData.append("real_name", params.real_name);
  formData.append("phone", params.phone);
  formData.append("detail", params.detail);

  // 嵌套的 address 对象需要拆开
  formData.append("address[province]", params.address.province);
  formData.append("address[city]", params.address.city);
  formData.append("address[district]", params.address.district);
  formData.append("address[city_id]", String(params.address.city_id));

  if (params.is_read_agreement !== undefined) {
    formData.append("is_read_agreement", String(params.is_read_agreement));
  }
  if (params.longitude) {
    formData.append("longitude", params.longitude);
  }
  if (params.latitude) {
    formData.append("latitude", params.latitude);
  }

  const res = await peiwantHttp.post<SuccessResponse>("/api/address/edit", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  console.log("[API] address/edit 响应:", { status: res.status, data: res.data });

  const body = res.data as SuccessResponse;
  if (body?.status !== undefined && body.status !== 200 && body.status !== 204) {
    throw new Error(body?.msg || "保存地址失败");
  }

  return body?.msg || "保存成功";
}

/**
 * 接口3：删除地址
 * POST /api/address/del
 * Body参数: id (地址ID)
 */
export async function deleteAddress(id: number): Promise<SuccessResponse["msg"]> {
  console.log("[API] 删除地址:", "/api/address/del", id);

  const formData = new FormData();
  formData.append("id", String(id));

  const res = await peiwantHttp.post<SuccessResponse>("/api/address/del", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const body = res.data as SuccessResponse;
  if (body?.status !== undefined && body.status !== 200 && body.status !== 204) {
    throw new Error(body?.msg || "删除地址失败");
  }

  return body?.msg || "删除成功";
}

/**
 * 接口4：设置默认地址
 * POST /api/address/default/set
 * Body参数: id (地址ID)
 */
export async function setDefaultAddress(id: number): Promise<SuccessResponse["msg"]> {
  console.log("[API] 设置默认地址:", "/api/address/default/set", id);

  const formData = new FormData();
  formData.append("id", String(id));

  const res = await peiwantHttp.post<SuccessResponse>("/api/address/default/set", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const body = res.data as SuccessResponse;
  if (body?.status !== undefined && body.status !== 200 && body.status !== 204) {
    throw new Error(body?.msg || "设置默认失败");
  }

  return body?.msg || "设置成功";
}

/**
 * 接口5：获取单个地址详情
 * GET /api/address/detail/{id}
 */
export async function fetchAddressDetail(id: number): Promise<AddressItem> {
  console.log("[API] 获取地址详情:", `/api/address/detail/${id}`);

  const res = await peiwantHttp.get<AddressDetailResponse>(`/api/address/detail/${id}`);

  const body = res.data as AddressDetailResponse;
  if (body?.status !== undefined && body.status !== 200 && body.status !== 204) {
    throw new Error(body?.msg || "获取地址详情失败");
  }

  return body?.data;
}
